import { CanDeactivateFn, Routes } from '@angular/router';
import { agentGuard } from './agent/agent.guard';
import { agentRoutePaths } from './agent/agent-route-paths';
import { activityTabRoutePaths, appRoutePaths } from './app-route-paths';
import { PageLayoutComponent } from './components/templates/page-layout/page-layout.component';
import { PermissionResource } from './connectors/backend';
import { adminGuard } from './guards/admin.guard';
import {
  companyRequiredGuard,
  noCompanyOnlyGuard,
} from './guards/company.guard';
import { AppSectionKey } from './guards/section-visibility.config';
import {
  activitiesSectionGuard,
  listAllPermissionGuard,
  resourceGuard,
  sectionVisibilityGuard,
  updateAllPermissionGuard,
} from './guards/section-visibility.guard';

const loadNotFoundPage = () =>
  import('./pages/not-found-page/not-found-page').then((m) => m.NotFoundPage);

interface PendingChangesComponent {
  canDeactivate: () => boolean | Promise<boolean>;
}

// The component is null when the route is left before it ever activated, and
// nothing unsaved can be pending in that case.
const confirmUnsavedChangesGuard: CanDeactivateFn<PendingChangesComponent> = (
  component,
) => component?.canDeactivate() ?? true;

export const routes: Routes = [
  {
    // Public, unauthenticated pages. Kept outside the auth-guarded tree below.
    path: 'public/dam/collections/:key',
    loadComponent: () =>
      import('./pages/public-collection-page/public-collection-page').then(
        (m) => m.PublicCollectionPage,
      ),
  },
  {
    // Public, unauthenticated single-asset share (`SingleAsset` collection).
    path: 'public/dam/assets/:key',
    loadComponent: () =>
      import('./pages/public-asset-page/public-asset-page').then(
        (m) => m.PublicAssetPage,
      ),
  },
  {
    // Dead end shown when no company exists yet. Sits outside the guarded tree
    // below, which would otherwise bounce it straight back to itself.
    path: appRoutePaths.noCompany,
    canActivate: [noCompanyOnlyGuard],
    loadComponent: () =>
      import('./pages/no-company-page/no-company-page').then(
        (m) => m.NoCompanyPage,
      ),
  },
  {
    path: '',
    // Every page below is scoped to a company, so none of them can render
    // without one.
    canActivate: [companyRequiredGuard],
    children: [
      // Header-less pages (own full-screen layout). Listed before the shell so
      // they win the match instead of falling through to it.
      {
        path: 'dam/assets/:id',
        canActivate: [sectionVisibilityGuard(AppSectionKey.Dam)],
        loadComponent: () =>
          import('./pages/dam-asset-page/dam-asset-page').then(
            (m) => m.DamAssetPage,
          ),
      },
      {
        path: 'gen-ai',
        canActivate: [resourceGuard([PermissionResource.AiGeneration])],
        loadComponent: () =>
          import('./pages/gen-ai-page/gen-ai-page').then((m) => m.GenAiPage),
      },
      {
        // Sales-agent app: its own tablet shell, so it sits outside the PIM one.
        path: agentRoutePaths.root,
        canActivate: [agentGuard],
        loadChildren: () =>
          import('./agent/agent.routes').then((m) => m.agentRoutes),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/settings-page/settings-page').then(
            (m) => m.SettingsPage,
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'account' },
          {
            path: 'account',
            loadComponent: () =>
              import('./pages/settings-page/routes/account-page/account-page').then(
                (m) => m.AccountPage,
              ),
          },
          {
            path: 'change-password',
            loadComponent: () =>
              import('./pages/settings-page/routes/account-page/routes/change-password-page/change-password-page').then(
                (m) => m.ChangePasswordPage,
              ),
          },
          {
            path: 'filters',
            canActivate: [sectionVisibilityGuard(AppSectionKey.Administration)],
            loadComponent: () =>
              import('./pages/settings-page/routes/filters-page/filters-page').then(
                (m) => m.FiltersPage,
              ),
          },
          {
            path: 'channels',
            canActivate: [adminGuard],
            loadComponent: () =>
              import('./pages/settings-page/routes/channels-page/channels-page').then(
                (m) => m.ChannelsPage,
              ),
          },
          {
            path: 'modules/agents',
            canActivate: [adminGuard],
            loadComponent: () =>
              import('./pages/settings-page/routes/agents-module-page/agents-module-page').then(
                (m) => m.AgentsModulePage,
              ),
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./pages/settings-page/routes/users-page/users-page').then(
                (m) => m.UsersPage,
              ),
            canActivate: [updateAllPermissionGuard(PermissionResource.User)],
          },
          {
            path: 'create-user',
            loadComponent: () =>
              import('./pages/settings-page/routes/users-page/routes/upsert-user-page/upsert-user-page').then(
                (m) => m.UpsertUserPage,
              ),
            canActivate: [updateAllPermissionGuard(PermissionResource.User)],
          },
          {
            path: 'user-details/:userId',
            loadComponent: () =>
              import('./pages/settings-page/routes/users-page/routes/upsert-user-page/upsert-user-page').then(
                (m) => m.UpsertUserPage,
              ),
            canActivate: [updateAllPermissionGuard(PermissionResource.User)],
          },
          {
            path: 'groups-and-roles',
            loadComponent: () =>
              import('./pages/settings-page/routes/groups-and-roles-page/groups-and-roles-page').then(
                (m) => m.GroupsAndRolesPage,
              ),
            canActivate: [updateAllPermissionGuard(PermissionResource.Role)],
          },
          {
            path: 'activity-log',
            loadComponent: () =>
              import('./pages/settings-page/routes/activity-log-page/activity-log-page').then(
                (m) => m.ActivityLogPage,
              ),
            canActivate: [
              listAllPermissionGuard(PermissionResource.ActivityLog),
            ],
          },
          {
            path: 'features/:featureId',
            canActivate: [
              updateAllPermissionGuard(PermissionResource.ProductFeature),
            ],
            loadComponent: () =>
              import('./pages/settings-page/routes/feature-details-page/feature-details-page').then(
                (m) => m.FeatureDetailsPage,
              ),
          },
          { path: '**', loadComponent: loadNotFoundPage },
        ],
      },
      // Pages sharing the persistent header, mounted once inside the page
      // layout so navigating between them does not recreate the header.
      {
        path: '',
        component: PageLayoutComponent,
        children: [
          {
            path: appRoutePaths.productsDrafts,
            canActivate: [
              sectionVisibilityGuard(AppSectionKey.ProductAndProductSheet),
            ],
            loadComponent: () =>
              import('./pages/drafts-products-page/drafts-products-page').then(
                (m) => m.DraftsProductsPage,
              ),
            children: [
              {
                path: 'bulk',
                loadComponent: () =>
                  import('./pages/product-details-page/product-details-page').then(
                    (m) => m.ProductDetailsPage,
                  ),
                canDeactivate: [confirmUnsavedChangesGuard],
              },
              {
                path: 'side/:productId',
                loadComponent: () =>
                  import('./pages/product-details-page/product-details-page').then(
                    (m) => m.ProductDetailsPage,
                  ),
                canDeactivate: [confirmUnsavedChangesGuard],
              },
            ],
          },
          {
            path: appRoutePaths.productsApproved,
            canActivate: [
              sectionVisibilityGuard(AppSectionKey.ProductAndProductSheet),
            ],
            loadComponent: () =>
              import('./pages/approved-products-page/approved-products-page').then(
                (m) => m.ApprovedProductsPage,
              ),
            children: [
              {
                path: 'bulk',
                loadComponent: () =>
                  import('./pages/product-details-page/product-details-page').then(
                    (m) => m.ProductDetailsPage,
                  ),
                canDeactivate: [confirmUnsavedChangesGuard],
              },
              {
                path: 'side/:productId',
                loadComponent: () =>
                  import('./pages/product-details-page/product-details-page').then(
                    (m) => m.ProductDetailsPage,
                  ),
                canDeactivate: [confirmUnsavedChangesGuard],
              },
            ],
          },
          {
            path: appRoutePaths.activities,
            canActivate: [activitiesSectionGuard],
            loadComponent: () =>
              import('./pages/activities-page/activities-page').then(
                (m) => m.ActivitiesPage,
              ),
            children: [
              // Imports is the landing tab; the page corrects it when the user
              // may not open it.
              {
                path: '',
                pathMatch: 'full',
                redirectTo: activityTabRoutePaths.imports,
              },
              {
                path: activityTabRoutePaths.imports,
                loadComponent: () =>
                  import('./pages/activities-page/import-activities-page/import-activities-page').then(
                    (m) => m.ImportActivitiesPage,
                  ),
                children: [
                  {
                    path: ':importId',
                    loadComponent: () =>
                      import('./components/organisms/drawers/import-details-drawer/import-details-drawer').then(
                        (m) => m.ImportDetailsDrawer,
                      ),
                  },
                ],
              },
              {
                path: activityTabRoutePaths.exports,
                loadComponent: () =>
                  import('./pages/activities-page/export-activities-page/export-activities-page').then(
                    (m) => m.ExportActivitiesPage,
                  ),
              },
              {
                path: activityTabRoutePaths.synchronizations,
                loadComponent: () =>
                  import('./pages/activities-page/sync-activities-page/sync-activities-page').then(
                    (m) => m.SyncActivitiesPage,
                  ),
                children: [
                  {
                    path: ':syncId',
                    loadComponent: () =>
                      import('./components/organisms/drawers/sync-products-drawer/sync-products-drawer').then(
                        (m) => m.SyncProductsDrawer,
                      ),
                  },
                ],
              },
              {
                path: activityTabRoutePaths.productUpdates,
                loadComponent: () =>
                  import('./pages/activities-page/product-update-activities-page/product-update-activities-page').then(
                    (m) => m.ProductUpdateActivitiesPage,
                  ),
                children: [
                  {
                    path: ':activityId',
                    loadComponent: () =>
                      import('./components/organisms/drawers/product-update-activity-drawer/product-update-activity-drawer').then(
                        (m) => m.ProductUpdateActivityDrawer,
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: appRoutePaths.lists + '/:listId',
            canActivate: [
              sectionVisibilityGuard(AppSectionKey.ProductAndProductSheet),
            ],
            loadComponent: () =>
              import('./pages/product-list-page/product-list-page').then(
                (m) => m.ProductListPage,
              ),
            children: [
              {
                path: 'side/:productId',
                loadComponent: () =>
                  import('./pages/product-details-page/product-details-page').then(
                    (m) => m.ProductDetailsPage,
                  ),
                canDeactivate: [confirmUnsavedChangesGuard],
              },
            ],
          },
          {
            path: 'dam',
            canActivate: [sectionVisibilityGuard(AppSectionKey.Dam)],
            loadComponent: () =>
              import('./pages/dam-page/dam-page').then((m) => m.DamPage),
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'all',
              },
              {
                path: 'all',
                loadComponent: () =>
                  import('./pages/dam-page/routes/dam-all-assets-page/dam-all-assets-page').then(
                    (m) => m.DamAllAssetsPage,
                  ),
              },
              {
                path: 'repository',
                loadComponent: () =>
                  import('./pages/dam-page/routes/dam-repository-page/dam-repository-page').then(
                    (m) => m.DamRepositoryPage,
                  ),
              },
              {
                path: 'collections',
                canActivate: [
                  resourceGuard([PermissionResource.DigitalAssetCollection]),
                ],
                loadComponent: () =>
                  import('./pages/dam-page/routes/dam-collections-page/dam-collections-page').then(
                    (m) => m.DamCollectionsPage,
                  ),
              },
              {
                path: 'collections/:id',
                canActivate: [
                  resourceGuard([PermissionResource.DigitalAssetCollection]),
                ],
                loadComponent: () =>
                  import('./pages/dam-page/routes/dam-collection-page/dam-collection-page').then(
                    (m) => m.DamCollectionPage,
                  ),
              },
              {
                path: 'trash',
                loadComponent: () =>
                  import('./pages/dam-page/routes/dam-trash-page/dam-trash-page').then(
                    (m) => m.DamTrashPage,
                  ),
              },
              { path: '**', loadComponent: loadNotFoundPage },
            ],
          },
          // Default child: without it an empty path renders the shell with a blank outlet.
          // The target's own section guard still redirects users without product access.
          {
            path: '',
            pathMatch: 'full',
            redirectTo: appRoutePaths.productsApproved,
          },
        ],
      },
      { path: '**', loadComponent: loadNotFoundPage },
    ],
  },
];
